import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, getCountFromServer, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import './Dashboard.css';

interface DashboardStats {
  totalUsers: number;
  totalBookings: number;
  bookingsByStatus: {
    completed: number;
    pending: number;
    cancelled: number;
    confirmed: number;
    pending_completion: number;
  };
  totalProviders: number;
  deposits: {
    total: number;
    completed: number;
    pending: number;
  };
  totalCustomers: number;
  totalRevenue: number;
  activeServices: number;
  totalCommission: number;
  totalProviderCommission: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalBookings: 0,
    bookingsByStatus: {
      completed: 0,
      pending: 0,
      cancelled: 0,
      confirmed: 0,
      pending_completion: 0
    },
    totalProviders: 0,
    deposits: {
      total: 0,
      completed: 0,
      pending: 0
    },
    totalCustomers: 0,
    totalRevenue: 0,
    activeServices: 0,
    totalCommission: 0,
    totalProviderCommission: 0
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      // Prepare queries for counts (fast, no document payload)
      const usersCol = collection(db, 'users');
      const bookingsCol = collection(db, 'bookings');
      const depositsCol = collection(db, 'deposit_history');
      const servicesCol = collection(db, 'service_items');
      const commissionsCol = collection(db, 'commissions');

      const providersQ = query(usersCol, where('role', '==', 'provider'));
      const customersQ = query(usersCol, where('role', '==', 'customer'));

      const bookingsCompletedQ = query(bookingsCol, where('status', '==', 'completed'));
      const bookingsPendingQ = query(bookingsCol, where('status', '==', 'pending'));
      const bookingsCancelledQ = query(bookingsCol, where('status', '==', 'cancelled'));
      const bookingsConfirmedQ = query(bookingsCol, where('status', '==', 'confirmed'));
      const bookingsPendingCompletionQ = query(bookingsCol, where('status', '==', 'pending_completion'));

      // Execute count queries in parallel
      const [
        usersCountSnap,
        totalBookingsCountSnap,
        completedCountSnap,
        pendingCountSnap,
        cancelledCountSnap,
        confirmedCountSnap,
        pendingCompletionCountSnap,
        providersCountSnap,
        customersCountSnap,
        servicesCountSnap
      ] = await Promise.all([
        getCountFromServer(usersCol),
        getCountFromServer(bookingsCol),
        getCountFromServer(bookingsCompletedQ),
        getCountFromServer(bookingsPendingQ),
        getCountFromServer(bookingsCancelledQ),
        getCountFromServer(bookingsConfirmedQ),
        getCountFromServer(bookingsPendingCompletionQ),
        getCountFromServer(providersQ),
        getCountFromServer(customersQ),
        getCountFromServer(servicesCol)
      ]);

      const totalUsers = usersCountSnap.data().count;
      const bookingStats = {
        total: totalBookingsCountSnap.data().count,
        completed: completedCountSnap.data().count,
        pending: pendingCountSnap.data().count,
        cancelled: cancelledCountSnap.data().count,
        confirmed: confirmedCountSnap.data().count,
        pending_completion: pendingCompletionCountSnap.data().count
      };
      const totalProviders = providersCountSnap.data().count;
      const totalCustomers = customersCountSnap.data().count;
      const activeServices = servicesCountSnap.data().count;

      // Revenue: only fetch completed bookings and sum finalPrice; also filter finalPrice > 0 if available
      const completedRevenueQuery = query(bookingsCol, where('status', '==', 'completed'));
      // If your dataset stores finalPrice numeric, optionally: where('finalPrice', '>', 0)

      // Deposits: fetch by status to avoid scanning all
      const depositsCompletedQ = query(depositsCol, where('status', '==', 'completed'));
      const depositsPendingQ = query(depositsCol, where('status', '==', 'pending'));

      // Commissions: sum ownerAmount and commissionAmount (no server aggregate for sums)
      // Optionally orderBy to use indexes efficiently
      const commissionsQ = query(commissionsCol, orderBy('createdAt', 'desc'));

      const [completedBookingsSnap, depositsCompletedSnap, depositsPendingSnap, commissionsSnap] = await Promise.all([
        getDocs(completedRevenueQuery),
        getDocs(depositsCompletedQ),
        getDocs(depositsPendingQ),
        getDocs(commissionsQ)
      ]);

      let totalRevenue = 0;
      completedBookingsSnap.forEach((d) => {
        const bd = d.data() as any;
        const price = bd.finalPrice || 0;
        totalRevenue += price;
      });

      const depositStats = {
        total: 0,
        completed: 0,
        pending: 0
      };
      depositsCompletedSnap.forEach((d) => {
        const v = (d.data() as any).amount || 0;
        depositStats.completed += v;
        depositStats.total += v;
      });
      depositsPendingSnap.forEach((d) => {
        const v = (d.data() as any).amount || 0;
        depositStats.pending += v;
        depositStats.total += v;
      });

      let totalCommission = 0;
      let totalProviderCommission = 0;
      commissionsSnap.forEach((d) => {
        const data = d.data() as any;
        totalCommission += data.ownerAmount || 0;
        totalProviderCommission += data.commissionAmount || 0;
      });

      setStats({
        totalUsers,
        totalBookings: bookingStats.total,
        bookingsByStatus: {
          completed: bookingStats.completed,
          pending: bookingStats.pending,
          cancelled: bookingStats.cancelled,
          confirmed: bookingStats.confirmed,
          pending_completion: bookingStats.pending_completion
        },
        totalProviders,
        deposits: depositStats,
        totalCustomers,
        totalRevenue,
        activeServices,
        totalCommission,
        totalProviderCommission
      });

    } catch (error) {
      console.error('Lỗi khi tải dữ liệu dashboard:', error);
      setError('Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('vi-VN', { 
      style: 'currency', 
      currency: 'VND' 
    });
  };

  if (loading) {
    return (
      <div className="dashboard-content">
        <div className="page-header">
          <h2>Bảng điều khiển quản trị</h2>
          <p className="page-subtitle">Tổng quan hệ thống Fconnect</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-message">
          {error}
          <button onClick={fetchDashboardStats} className="retry-button">
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h2>Bảng điều khiển quản trị</h2>
        <p className="page-subtitle">Tổng quan hệ thống Fconnect</p>
      </div>
      
      {/* Các chỉ số thống kê - Layout 7 cột */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <h3>Tổng người dùng</h3>
            <div className="stat-value">{stats.totalUsers.toLocaleString()}</div>
            <div className="stat-breakdown">
              <span>{stats.totalCustomers} khách hàng • {stats.totalProviders} nhà cung cấp</span>
            </div>
          </div>
        </div>
        
        <div className="stat-card success">
          <div className="stat-icon">📋</div>
          <div className="stat-info">
            <h3>Booking</h3>
            <div className="stat-value">{stats.totalBookings.toLocaleString()}</div>
            <div className="stat-breakdown statuses">
              <span className="status-pill completed">{stats.bookingsByStatus.completed} hoàn thành</span>
              <span className="status-pill confirmed">{stats.bookingsByStatus.confirmed} đã duyệt</span>
              <span className="status-pill pending">{stats.bookingsByStatus.pending} chờ duyệt</span>
              <span className="status-pill pending-completion">{stats.bookingsByStatus.pending_completion} chờ hoàn thành</span>
              <span className="status-pill cancelled">{stats.bookingsByStatus.cancelled} đã hủy</span>
            </div>
          </div>
        </div>
        
        <div className="stat-card warning">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <h3>Doanh thu</h3>
            <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
            <div className="stat-breakdown">
              <span>Tổng tiền từ {stats.bookingsByStatus.completed} booking hoàn thành</span>
            </div>
          </div>
        </div>

        <div className="stat-card danger">
          <div className="stat-icon">💸</div>
          <div className="stat-info">
            <h3>Thu nhập sàn</h3>
            <div className="stat-value">{formatCurrency(stats.totalCommission)}</div>
            <div className="stat-breakdown">
              <span>Tổng thu nhập từ tất cả booking</span>
            </div>
          </div>
        </div>

        <div className="stat-card purple">
          <div className="stat-icon">💵</div>
          <div className="stat-info">
            <h3>Hoa hồng nhà cung cấp</h3>
            <div className="stat-value">{formatCurrency(stats.totalProviderCommission)}</div>
            <div className="stat-breakdown">
              <span>Tổng hoa hồng nhà cung cấp từ tất cả booking</span>
            </div>
          </div>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">💳</div>
          <div className="stat-info">
            <h3>Tổng tiền nạp</h3>
            <div className="stat-value">{formatCurrency(stats.deposits.total)}</div>
            <div className="stat-breakdown">
              <span>{formatCurrency(stats.deposits.completed)} đã hoàn thành • {formatCurrency(stats.deposits.pending)} đang chờ</span>
            </div>
          </div>
        </div>
        
        <div className="stat-card info">
          <div className="stat-icon">🔧</div>
          <div className="stat-info">
            <h3>Dịch vụ</h3>
            <div className="stat-value">{stats.activeServices.toLocaleString()}</div>
            <div className="stat-breakdown">
              <span>Đang hoạt động</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 